/**
 * Pure helpers for the Learning Coach pages: plain-word labels for the numbers the learner model
 * keeps, routes for suggestions and cited sources, and the parsing of a cited answer. Nothing here
 * talks to the network, so all of it is unit tested.
 */
import type { Route } from '../router';
import type { IconName } from '../components/ui/Icon';
import type { CoachSuggestion, CoachSuggestionKind, Explanation, LearnerConfidence, LearnerKnowledgeRow, LearnerMisconceptionView, LearnerProfileView, LearnerTrend, PassageSource } from '../api/coach';

export type LearnerHabitsView = LearnerProfileView['habits'];

/* ------------------------------------------------------------------ suggestions */

export interface SuggestionKindMeta {
  label: string;
  icon: IconName;
  /** The Start button's words. */
  action: string;
}

const KIND_META: Record<CoachSuggestionKind, SuggestionKindMeta> = {
  lesson: { label: 'Lesson', icon: 'book', action: 'Start the lesson' },
  'knowledge-topic': { label: 'Knowledge topic', icon: 'lightbulb', action: 'Read the topic' },
  lab: { label: 'Lab', icon: 'terminal', action: 'Open the lab' },
  'code-lab': { label: 'Code lab', icon: 'code', action: 'Open the code lab' },
  'vm-lab': { label: 'VM lab', icon: 'server', action: 'Open the VM lab' },
  scenario: { label: 'Scenario', icon: 'briefcase', action: 'Open the scenario' },
  project: { label: 'Project', icon: 'folder', action: 'Open the project' },
  'cheat-sheet': { label: 'Cheat sheet', icon: 'list', action: 'Open the cheat sheet' },
  assessment: { label: 'Assessment', icon: 'clipboard', action: 'Open the assessment' },
  review: { label: 'Review', icon: 'clock', action: 'Review the skill' },
  rest: { label: 'A different angle', icon: 'compass', action: 'See the skill' },
  interview: { label: 'Interview practice', icon: 'chat', action: 'Open interview prep' },
};

export function suggestionKindMeta(kind: string): SuggestionKindMeta {
  return KIND_META[kind as CoachSuggestionKind] ?? { label: kind.replace(/[-_]/g, ' '), icon: 'arrow-right', action: 'Open it' };
}

/** Where a suggestion (or one of its alternatives) opens. `review` and `rest` target a skill; a `guide-next-level` review opens the skill's guide. */
export function suggestionRoute(kind: string, targetId: string, careerId?: string, ruleId?: string): Route {
  if (ruleId === 'guide-next-level' && kind === 'review') return { page: 'guide', id: targetId };
  switch (kind) {
    case 'lesson':
      return { page: 'learn-lesson', id: targetId };
    case 'knowledge-topic':
      return { page: 'knowledge-topic', id: targetId };
    case 'lab':
      return { page: 'learn-lab', id: targetId };
    case 'code-lab':
      return { page: 'codelab', id: targetId };
    case 'vm-lab':
      return { page: 'vmlab', id: targetId };
    case 'scenario':
      return careerId ? { page: 'scenario', id: targetId, career: careerId } : { page: 'scenario', id: targetId };
    case 'project':
      return { page: 'project', id: targetId };
    case 'cheat-sheet':
      return { page: 'cheatsheet', id: targetId };
    case 'assessment':
      return { page: 'assessment', id: targetId };
    case 'review':
    case 'rest':
      return { page: 'skill', id: targetId };
    case 'interview':
      return { page: 'interview', career: targetId || undefined };
    default:
      return { page: 'learn' };
  }
}

/** The rule's confidence as words, never as a score. */
export function confidenceWords(confidence: number): 'Likely helpful' | 'Worth trying' {
  return confidence >= 0.7 ? 'Likely helpful' : 'Worth trying';
}

/** The first premises of a suggestion, for a compact card. */
export function becausePreview(s: Pick<CoachSuggestion, 'because'>, count = 2): { shown: string[]; more: number } {
  return { shown: s.because.slice(0, count), more: Math.max(0, s.because.length - count) };
}

export interface MinuteChoice {
  id: string;
  label: string;
  minutes?: number;
}

/** The minutes-available control. "Any" sends nothing, so the daily minutes in the learner's goals apply when set. */
export const MINUTE_CHOICES: MinuteChoice[] = [
  { id: '15', label: '15 min', minutes: 15 },
  { id: '30', label: '30 min', minutes: 30 },
  { id: '60', label: '60 min', minutes: 60 },
  { id: 'any', label: 'Any' },
];

export function minutesLine(minutes?: number): string {
  if (minutes === undefined || minutes <= 0) return '';
  return minutes < 60 ? `About ${Math.round(minutes)} min` : `About ${Math.round((minutes / 60) * 10) / 10} h`;
}

/* ------------------------------------------------------------------ answers */

export type AnswerSegment = { kind: 'text'; text: string } | { kind: 'cite'; n: number; valid: boolean };

/**
 * Split a composed answer into paragraphs of text and citation markers. `[n]` is valid when a
 * source exists for it (`sources[n - 1]`); an invalid marker is kept as plain text.
 */
export function parseAnswer(answer: string, sourceCount: number): AnswerSegment[][] {
  return answer
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => {
      const out: AnswerSegment[] = [];
      let last = 0;
      for (const m of p.matchAll(/\[(\d+)\]/g)) {
        const at = m.index ?? 0;
        if (at > last) out.push({ kind: 'text', text: p.slice(last, at) });
        const n = Number(m[1]);
        out.push({ kind: 'cite', n, valid: n >= 1 && n <= sourceCount });
        last = at + m[0].length;
      }
      if (last < p.length) out.push({ kind: 'text', text: p.slice(last) });
      return out;
    });
}

/** Every distinct citation number used in an answer, in order. */
export function citedNumbers(answer: string): number[] {
  const seen: number[] = [];
  for (const m of answer.matchAll(/\[(\d+)\]/g)) {
    const n = Number(m[1]);
    if (!seen.includes(n)) seen.push(n);
  }
  return seen;
}

const SOURCE_KIND_LABEL: Record<string, string> = {
  lesson: 'Lesson',
  'knowledge-topic': 'Knowledge topic',
  'cheat-sheet': 'Cheat sheet',
  resource: 'Resource',
  'why-wrong': 'Why an option is wrong',
  glossary: 'Glossary',
  lab: 'Lab',
};

export const sourceKindLabel = (kind: string): string => SOURCE_KIND_LABEL[kind] ?? kind.replace(/[-_]/g, ' ');

/** Lessons have `lesson-` ids; everything else a question or glossary term belongs to is a knowledge topic. */
export function ownerRoute(ownerId: string): Route {
  return ownerId.startsWith('lesson-') ? { page: 'learn-lesson', id: ownerId } : { page: 'knowledge-topic', id: ownerId };
}

export const isSafeUrl = (url: string | undefined): url is string => typeof url === 'string' && /^https:\/\//i.test(url);

export interface SourceLink {
  /** An in-app route, or an external https URL (open in a new tab). */
  route?: Route;
  url?: string;
}

/** Where a cited source opens. Resources open their external page when the answer lists a safe URL for them. */
export function sourceLink(source: PassageSource, readMore: Explanation['readMore'] = []): SourceLink {
  const owner = source.id.split('#')[0] ?? source.id;
  switch (source.kind) {
    case 'lesson':
      return { route: { page: 'learn-lesson', id: source.id } };
    case 'knowledge-topic':
      return { route: { page: 'knowledge-topic', id: source.id } };
    case 'cheat-sheet':
      return { route: { page: 'cheatsheet', id: source.id } };
    case 'lab':
      return { route: { page: 'learn-lab', id: source.id } };
    case 'why-wrong':
    case 'glossary':
      return { route: ownerRoute(owner) };
    case 'resource': {
      const url = readMore.find((r) => r.id === source.id)?.url;
      return isSafeUrl(url) ? { url } : { route: { page: 'resources', q: source.title } };
    }
    default:
      return { route: ownerRoute(owner) };
  }
}

/** Where a "read more" entry opens. */
export function readMoreLink(item: Explanation['readMore'][number]): SourceLink {
  if (item.kind === 'resource') return isSafeUrl(item.url) ? { url: item.url } : { route: { page: 'resources', q: item.title } };
  if (item.kind === 'lesson') return { route: { page: 'learn-lesson', id: item.id } };
  if (item.kind === 'cheat-sheet') return { route: { page: 'cheatsheet', id: item.id } };
  return { route: { page: 'knowledge-topic', id: item.id } };
}

/** The badge under an answer: who wrote its words. */
export function phrasedByLabel(phrasedBy: string): string {
  if (!phrasedBy || phrasedBy === 'coach') return 'Answered by the coach';
  if (phrasedBy === 'ollama') return 'Phrased by a local model (Ollama)';
  if (phrasedBy === 'claude') return 'Phrased by a model (Claude)';
  return `Phrased by a model (${phrasedBy})`;
}

export interface RecentQuestion {
  query: string;
  at: string;
}

/** Newest first, no repeats (case-insensitive), at most `max`. */
export function pushRecent(list: RecentQuestion[], query: string, at: string, max = 5): RecentQuestion[] {
  const q = query.trim();
  if (!q) return list;
  return [{ query: q, at }, ...list.filter((x) => x.query.toLowerCase() !== q.toLowerCase())].slice(0, max);
}

export const ASK_MAX = 500;

/** The prefilled question for "Ask the coach why" after a wrong answer. */
export function wrongAnswerQuery(prompt: string, chosen?: string): string {
  const base = chosen ? `Why is "${chosen}" wrong? ${prompt}` : `Why is my answer wrong? ${prompt}`;
  return base.length > ASK_MAX ? `${base.slice(0, ASK_MAX - 1).trimEnd()}…` : base;
}

/** A knowledge-check question as the coach names it: `ownerId#questionId`. */
export const questionRef = (ownerId: string, questionId: string): string => `${ownerId}#${questionId}`.slice(0, 240);

/* ------------------------------------------------------------------ learner model */

/** Estimated knowledge (pKnown) as words. */
export function knowledgeWords(pKnown: number): string {
  if (pKnown < 0.3) return 'Just starting';
  if (pKnown < 0.6) return 'Partly known';
  if (pKnown < 0.85) return 'Mostly known';
  return 'Well known';
}

export function knowledgeTone(pKnown: number): 'bad' | 'warn' | 'ok' {
  return pKnown < 0.3 ? 'bad' : pKnown < 0.6 ? 'warn' : 'ok';
}

const CONFIDENCE_WORDS: Record<LearnerConfidence, string> = { low: 'Low: few answers so far', medium: 'Medium', high: 'High: many answers' };
export const confidenceLabel = (c: LearnerConfidence | string): string => CONFIDENCE_WORDS[c as LearnerConfidence] ?? String(c);

const TREND_WORDS: Record<LearnerTrend, string> = { up: 'Rising', flat: 'Steady', down: 'Falling' };
export const trendLabel = (t: LearnerTrend | string): string => TREND_WORDS[t as LearnerTrend] ?? String(t);
export const trendIcon = (t: LearnerTrend | string): IconName => (t === 'up' ? 'arrow-right' : t === 'down' ? 'warning' : 'check');

/** Recall is 0 until the skill was recalled correctly once. */
export function recallLabel(recall: number, observations = 1): string {
  if (recall <= 0) return observations > 0 ? 'Not recalled yet' : 'No answers yet';
  return `${Math.round(recall * 100)}%`;
}

const dayOf = (iso: string) => iso.slice(0, 10);

/** "Review by" as words: a date, "overdue since" a past date, or nothing to review yet. */
export function reviewByLabel(reviewBy: string | undefined, now: string, formatDay: (iso: string) => string = dayOf): { text: string; overdue: boolean } {
  if (!reviewBy) return { text: 'Nothing to review yet', overdue: false };
  if (Date.parse(reviewBy) <= Date.parse(now)) return { text: `Overdue since ${formatDay(reviewBy)}`, overdue: true };
  return { text: formatDay(reviewBy), overdue: false };
}

const pctWords = (x: number) => `${Math.round(x * 100)}%`;
const KIND_WORDS: Record<string, string> = {
  lesson: 'Lessons',
  'lesson-question': 'Lesson questions',
  'knowledge-question': 'Knowledge-check questions',
  'knowledge-check': 'Knowledge checks',
  lab: 'Labs',
  'code-lab': 'Code labs',
  'code-test': 'Code lab tests',
  'vm-lab': 'VM labs',
  'vm-task': 'VM lab tasks',
  scenario: 'Scenarios',
  'scenario-criterion': 'Scenario criteria',
  project: 'Projects',
  interview: 'Interviews',
  assessment: 'Assessments',
  'practice-question': 'Practice questions',
};
export const activityKindLabel = (kind: string): string => KIND_WORDS[kind] ?? kind.replace(/[-_]/g, ' ');

/** A 24-hour UTC bucket as a local time range label ("14:00 to 15:00"). */
export function hourLabel(utcHour: number, toLocal: (utcHour: number) => number = (h) => new Date(Date.UTC(2020, 0, 1, h)).getHours()): string {
  const h = toLocal(utcHour);
  const two = (n: number) => String(n).padStart(2, '0');
  return `${two(h)}:00 to ${two((h + 1) % 24)}:00`;
}

/** The busiest UTC hour, or undefined when nothing was recorded. */
export function peakHour(activeHours: number[]): number | undefined {
  let best: number | undefined;
  activeHours.forEach((n, h) => {
    if (n > 0 && (best === undefined || n > (activeHours[best] ?? 0))) best = h;
  });
  return best;
}

export interface HabitLine {
  label: string;
  value: string;
}

/** The habits the coach tracks, in plain words. Figures that have no data yet are left out. */
export function habitLines(h: LearnerHabitsView, toLocal?: (utcHour: number) => number): HabitLine[] {
  const out: HabitLine[] = [];
  if (h.totalMinutes > 0) out.push({ label: 'Recorded practice', value: `About ${Math.round(h.totalMinutes)} minutes` });
  if (h.sessionMinutes > 0) out.push({ label: 'Typical session', value: `About ${Math.round(h.sessionMinutes)} minutes` });
  const peak = peakHour(h.activeHours ?? []);
  if (peak !== undefined) out.push({ label: 'Most active', value: hourLabel(peak, toLocal) });
  if (h.hintReliance > 0) out.push({ label: 'Hints', value: `Used on ${pctWords(h.hintReliance)} of attempts` });
  if (h.retryToPass > 0) out.push({ label: 'Passing', value: h.retryToPass <= 1.05 ? 'Usually on the first try' : `Usually on attempt ${Math.round(h.retryToPass * 10) / 10}` });
  if (h.paceRatio > 0) out.push({ label: 'Pace', value: h.paceRatio < 0.9 ? 'Faster than the estimates' : h.paceRatio > 1.1 ? 'Slower than the estimates, which is fine' : 'Close to the estimates' });
  if (h.guidedPassRate > 0) out.push({ label: 'Guided mode', value: `${pctWords(h.guidedPassRate)} passed` });
  if (h.independentPassRate > 0) out.push({ label: 'Independent mode', value: `${pctWords(h.independentPassRate)} passed` });
  return out;
}

/** Pass rates by activity kind, busiest first. */
export function kindRows(h: LearnerHabitsView): { kind: string; label: string; attempts: number; passRate: string }[] {
  return Object.entries(h.byKind ?? {})
    .filter(([, s]) => s.attempts > 0)
    .sort((a, b) => b[1].attempts - a[1].attempts || a[0].localeCompare(b[0]))
    .map(([kind, s]) => ({ kind, label: activityKindLabel(kind), attempts: s.attempts, passRate: pctWords(s.passRate) }));
}

/** What the coach knows about one skill: its knowledge row and the wrong answers counted against it. */
export function skillCoachView(profile: Pick<LearnerProfileView, 'knowledge' | 'misconceptions'> | undefined, skillId: string): { knowledge?: LearnerKnowledgeRow; misconceptions: LearnerMisconceptionView[] } {
  if (!profile) return { misconceptions: [] };
  return {
    knowledge: profile.knowledge.find((k) => String(k.skillId) === skillId),
    misconceptions: profile.misconceptions.filter((m) => m.skillIds.includes(skillId)),
  };
}

/** A profile with nothing in it yet: a new learner, or right after a reset. */
export const isEmptyProfile = (p: Pick<LearnerProfileView, 'eventCount' | 'knowledge'>): boolean => p.eventCount === 0 && p.knowledge.length === 0;

/** Goals editor: the minutes field as the API wants it (5 to 600, or null to clear). */
export function parseMinutes(raw: string): number | null | 'invalid' {
  const t = raw.trim();
  if (!t) return null;
  const n = Number(t);
  if (!Number.isInteger(n) || n < 5 || n > 600) return 'invalid';
  return n;
}
